/**
 * Rubrique « Marges et rentabilité » (back-office). Données INTERNES : écrans réservés à `marge.lire` ;
 * paramétrage des coûts `couts.parametrer` ★ ; retours / avoirs `retour.enregistrer` ★.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Choice, Modal, Pagination, SearchField, SelectField, Table, Tabs, TextField, useToast } from '@myron/design-system';
import { Plus, RotateCcw, Save, Trash2, Undo2, Wrench } from 'lucide-react';
import type { S, Page } from '@myron/contracts';
import { ApiErreur, get, nouvelleCleIdempotence, post, put } from '../../../shared/api/client';
import { SiPermission, useSession } from '../../../shared/auth/session';
import { EntetePage, ErreurFormulaire, EtatRequete, StatutBadge } from '../../../shared/ui/composants';
import { dateCourte, dateHeure, euros, LIBELLE_CONDITIONNEMENT, LIBELLE_MOYEN } from '../../../shared/ui/format';
import { BarreFiltres, useClientsSelecteur, versCentimes } from '../commercial/commun';
import { OngletAnalyses } from './Analyses';

type LigneProduit = S<'MargeProduitLigne'>;
type MargesProduit = S<'MargesProduit'>;
type Frais = S<'FraisCout'>;
type ParametresCouts = S<'ParametresCouts'>;
type MargesCommande = S<'MargesCommande'>;
type RentabiliteClient = S<'RentabiliteClient'>;
/** Champs ajoutés par A5 (fragment docs/contrats/fragments/analyses.yaml, fusion au contrat par l'orchestrateur). */
type Rentabilite = S<'Rentabilite'> & { margeContributiveCentimes?: number; tauxMargeContributiveBp?: number | null; transportEstimeNbCommandes?: number; beneficeNetComptableCentimes?: null };

/** Taux de marge (points de base) → « 20,6 % » ; 0 % est un vrai 0 % ; null → tiret. */
export const taux = (bp: number | null | undefined) => (bp === null || bp === undefined ? '—' : `${(bp / 100).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`);
const montant = (c: number | null | undefined) => (c === null || c === undefined ? '—' : euros(c));

/** « Marge à compléter » : jamais un 0 trompeur. */
export function CelluleMarge({ marge, statut }: { marge: number | null | undefined; statut?: string }) {
  if (statut === 'A_COMPLETER' || marge === null || marge === undefined) return <Badge ton="alerte">Marge à compléter</Badge>;
  return <span className="my-num" style={marge < 0 ? { color: 'var(--erreur)', fontWeight: 600 } : undefined}>{euros(marge)}</span>;
}

const SOURCE_ACHAT: Record<string, string> = {
  PALETTE_FOURNISSEUR: 'Coût fournisseur propre à la palette',
  CARTONS: 'Calculé : nombre de cartons × coût carton',
  CONDITIONNEMENT_FOURNISSEUR: 'Prix fournisseur du conditionnement',
  LOT: 'Coût du lot',
  ABSENT: 'Aucun prix d’achat',
};

type Onglet = 'produits' | 'clients' | 'analyses' | 'parametres';

export function PageMarges() {
  const [params, setParams] = useSearchParams();
  const onglet = (params.get('onglet') as Onglet) ?? 'produits';
  return (
    <>
      <EntetePage titre="Marges et rentabilité" sousTitre="Données internes réservées aux rôles autorisés — jamais visibles des clients." />
      <Tabs<Onglet>
        label="Rubriques des marges"
        actif={onglet}
        onChange={(o) => setParams({ onglet: o })}
        onglets={[{ id: 'produits', libelle: 'Produits et conditionnements' }, { id: 'clients', libelle: 'Rentabilité par client' }, { id: 'analyses', libelle: 'Analyses' }, { id: 'parametres', libelle: 'Paramètres de coûts' }]}
      />
      <div role="tabpanel">
        {onglet === 'produits' && <OngletProduits />}
        {onglet === 'clients' && <OngletClients />}
        {onglet === 'analyses' && <OngletAnalyses />}
        {onglet === 'parametres' && <OngletParametres />}
      </div>
    </>
  );
}

function OngletProduits() {
  const nav = useNavigate();
  const [saisie, setSaisie] = useState('');
  const [q, setQ] = useState('');
  const [aCompleter, setACompleter] = useState(false);
  const [page, setPage] = useState(1);
  const r = useQuery({
    queryKey: ['admin', 'marges', 'produits', q, aCompleter, page],
    queryFn: () => get<Page<LigneProduit> & { nbACompleter: number }>('/admin/marges/produits', { q, statut: aCompleter ? 'A_COMPLETER' : undefined, page, taille: 50 }),
  });
  return (
    <div className="my-pile">
      <BarreFiltres>
        <form role="search" className="my-pile my-pile--serre" onSubmit={(e) => { e.preventDefault(); setPage(1); setQ(saisie.trim()); }}>
          <SearchField label="Rechercher un produit (libellé ou référence)" placeholder="Produit ou référence…" value={saisie} onChange={(e) => setSaisie(e.target.value)} />
          <Button type="submit" variante="secondaire" compact>Rechercher</Button>
        </form>
        <Choice label="Seulement les marges à compléter" checked={aCompleter} onChange={(e) => { setPage(1); setACompleter(e.target.checked); }} />
      </BarreFiltres>
      {r.data && r.data.nbACompleter > 0 && <Alert ton="alerte" titre={`${r.data.nbACompleter} ligne(s) avec « marge à compléter »`}>Un prix d’achat manque : renseignez la référence fournisseur (Achats → Fournisseurs) ou un coût palette.</Alert>}
      <EtatRequete requete={r} vide={(d) => !d.donnees.length} titreVide="Aucun produit" enfants={(d) => (
        <>
          <Table<LigneProduit>
            legende="Marges par produit et conditionnement"
            lignes={d.donnees}
            cle={(l) => `${l.produitId}-${l.conditionnementType}`}
            onLigne={(l) => nav(`/admin/marges/produits/${l.produitId}`)}
            colonnes={[
              { cle: 'p', titre: 'Produit', rendu: (l) => <Link to={`/admin/marges/produits/${l.produitId}`} onClick={(e) => e.stopPropagation()}>{l.libelle}<br /><span className="my-legende my-secondaire">{l.sku}</span></Link> },
              { cle: 'c', titre: 'Conditionnement', rendu: (l) => `${LIBELLE_CONDITIONNEMENT[l.conditionnementType!]} (${l.unitesBase} u.)` },
              { cle: 'a', titre: 'Achat HT', num: true, rendu: (l) => montant(l.prixAchatHtCentimes) },
              { cle: 'r', titre: 'Coût de revient HT', num: true, rendu: (l) => montant(l.coutRevientHtCentimes) },
              { cle: 'v', titre: 'Vente nette HT (base)', num: true, rendu: (l) => montant(l.prixVenteNetHtCentimes) },
              { cle: 'm', titre: 'Marge', num: true, rendu: (l) => <CelluleMarge marge={l.margeCentimes} statut={l.statut} /> },
              { cle: 't', titre: 'Marge %', num: true, rendu: (l) => (l.statut === 'A_COMPLETER' ? '—' : taux(l.tauxBp)) },
            ]}
          />
          <Pagination page={d.page} taille={d.taille} total={d.total} onPage={setPage} />
        </>
      )} />
    </div>
  );
}

export function PageMargeProduit() {
  const { id = '' } = useParams();
  const [client, setClient] = useState('');
  const clients = useClientsSelecteur();
  const r = useQuery({ queryKey: ['admin', 'marges', 'produit', id, client], queryFn: () => get<MargesProduit>(`/admin/marges/produits/${id}`, { entrepriseId: client || undefined }) });
  return (
    <EtatRequete requete={r} enfants={(p) => (
      <div className="my-pile">
        <EntetePage titre={p.libelle!} fil={[{ libelle: 'Marges et rentabilité', vers: '/admin/marges' }, { libelle: p.sku! }]} sousTitre="Coût de revient, prix de vente net et marge prévisionnelle selon les grilles tarifaires." />
        <div style={{ maxWidth: 480 }}>
          <SelectField
            label="Appliquer les remises propres à un client"
            value={client}
            onChange={(e) => setClient(e.target.value)}
            options={[{ value: '', label: 'Aucun client — grilles seulement' }, ...((clients.data?.donnees ?? []).filter((c) => c.statut === 'ACTIVE').map((c) => ({ value: c.id, label: `${c.reference} — ${c.raisonSociale}` })))]}
          />
        </div>
        {p.conditionnements!.map((c) => <CarteConditionnement key={c.type} produitId={p.produitId!} c={c} />)}
      </div>
    )} />
  );
}

function CarteConditionnement({ produitId, c }: { produitId: string; c: NonNullable<MargesProduit['conditionnements']>[number] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { peut } = useSession();
  const [saisie, setSaisie] = useState('');
  const detail = c.achat?.detail;
  const maj = useMutation({
    mutationFn: (v: number | null) => put<MargesProduit>(`/admin/marges/produits/${produitId}/cout-palette`, { prixAchatPaletteCentimes: v, motif: 'Saisie back-office' }),
    onSuccess: () => { setSaisie(''); void qc.invalidateQueries({ queryKey: ['admin', 'marges'] }); toast({ ton: 'succes', titre: 'Coût palette enregistré', texte: 'Marges prévisionnelles recalculées ; les ventes passées gardent leur coût figé.' }); },
  });
  const valeur = versCentimes(saisie);
  return (
    <Card as="section" aria-labelledby={`t-${c.type}`}>
      <div className="my-pile">
        <h2 id={`t-${c.type}`} className="my-titre-3">{LIBELLE_CONDITIONNEMENT[c.type!]} <span className="my-secondaire my-petit">— {c.unitesBase} unités{c.cartonsParPalette ? ` · ${c.cartonsParPalette} cartons` : ''}{c.vendable === false ? ' · non vendable' : ''}</span></h2>
        <div className="my-grille-2">
          <dl className="my-dl">
            <dt>Prix d’achat HT</dt>
            <dd className="my-num" data-testid={`achat-${c.type}`}>{montant(c.achat?.prixAchatHtCentimes)}</dd>
            <dt>Origine</dt>
            <dd>{SOURCE_ACHAT[c.achat?.source ?? 'ABSENT']}</dd>
            {c.type === 'PALETTE' && detail && (
              <>
                <dt>Calcul par cartons</dt>
                <dd className="my-num">{detail.coutCartonCentimes ? `${detail.cartonsParPalette} × ${euros(detail.coutCartonCentimes)} = ${montant(detail.coutPaletteCalculeCentimes)}` : 'Coût carton inconnu'}</dd>
                <dt>Coût fournisseur palette</dt>
                <dd className="my-num">{detail.coutPaletteFournisseurCentimes ? euros(detail.coutPaletteFournisseurCentimes) : 'Non renseigné'}</dd>
              </>
            )}
            {(c.coutRevient?.frais ?? []).map((f) => (
              <Fragment key={f.code}><dt>+ {f.libelle}</dt><dd className="my-num">{euros(f.montantCentimes)}</dd></Fragment>
            ))}
            <dt><strong>Coût de revient HT</strong></dt>
            <dd className="my-prix" data-testid={`revient-${c.type}`}>{c.coutRevient?.coutRevientHtCentimes === null ? <Badge ton="alerte">Marge à compléter</Badge> : montant(c.coutRevient?.coutRevientHtCentimes)}</dd>
          </dl>
          {c.type === 'PALETTE' && peut('couts.parametrer') && (
            <form className="my-form" onSubmit={(e) => { e.preventDefault(); if (Number.isFinite(valeur) && valeur > 0) maj.mutate(valeur); }}>
              <TextField label="Coût fournisseur propre à la palette (€ HT)" inputMode="decimal" value={saisie} onChange={(e) => setSaisie(e.target.value)} aide="Remplace le calcul « cartons × coût carton » pour le fournisseur principal." />
              <ErreurFormulaire erreur={maj.error} />
              <div className="my-ligne">
                <Button type="submit" icone={Save} chargement={maj.isPending && maj.variables !== null} disabled={!(Number.isFinite(valeur) && valeur > 0)}>Enregistrer le coût palette</Button>
                {detail?.coutPaletteFournisseurCentimes ? <Button variante="tertiaire" icone={RotateCcw} chargement={maj.isPending && maj.variables === null} onClick={() => maj.mutate(null)}>Revenir au calcul par cartons</Button> : null}
              </div>
            </form>
          )}
        </div>
        <Table
          legende={`Marge prévisionnelle par grille — ${LIBELLE_CONDITIONNEMENT[c.type!]}`}
          lignes={c.grilles ?? []}
          cle={(g) => g.code!}
          colonnes={[
            { cle: 'g', titre: 'Grille', rendu: (g) => <>{g.libelle}{g.type === 'CLIENT' && <> <Badge ton="accent">Client</Badge></>}</> },
            { cle: 'p', titre: 'Prix de vente net HT', num: true, rendu: (g) => montant(g.prixVenteNetHtCentimes) },
            { cle: 'r', titre: 'Remise', num: true, rendu: (g) => (g.remiseBp ? taux(g.remiseBp) : g.source === 'CLIENT' ? 'Tarif négocié' : g.source === 'GROUPE' ? 'Tarif groupe' : '—') },
            { cle: 'm', titre: 'Marge', num: true, rendu: (g) => <CelluleMarge marge={g.margeCentimes} statut={g.statut} /> },
            { cle: 't', titre: 'Marge %', num: true, rendu: (g) => (g.statut === 'A_COMPLETER' ? '—' : taux(g.tauxBp)) },
            { cle: 'v', titre: 'Paliers de volume', rendu: (g) => (g.paliers?.length ? g.paliers.map((x) => `≥ ${x.seuilQuantite} : ${taux(x.tauxBp)}`).join(' · ') : '—') },
          ]}
        />
      </div>
    </Card>
  );
}

// ───────────────────────────── Rentabilité ─────────────────────────────

const debutMois = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };

function FiltrePeriode({ du, au, onChange }: { du: string; au: string; onChange: (du: string, au: string) => void }) {
  return (
    <BarreFiltres>
      <TextField label="Du" type="date" value={du} onChange={(e) => onChange(e.target.value, au)} />
      <TextField label="Au" type="date" value={au} onChange={(e) => onChange(du, e.target.value)} />
      <div className="my-ligne">
        <Button compact variante="tertiaire" onClick={() => onChange(debutMois(), '')}>Mois en cours</Button>
        <Button compact variante="tertiaire" onClick={() => onChange(`${new Date().getFullYear()}-01-01`, '')}>Année</Button>
        <Button compact variante="tertiaire" onClick={() => onChange('', '')}>Tout</Button>
      </div>
    </BarreFiltres>
  );
}

function OngletClients() {
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const nav = useNavigate();
  const r = useQuery({ queryKey: ['admin', 'marges', 'clients', du, au], queryFn: () => get<{ donnees: S<'RentabiliteClientLigne'>[] }>('/admin/marges/clients', { du, au }) });
  return (
    <div className="my-pile">
      <FiltrePeriode du={du} au={au} onChange={(a, b) => { setDu(a); setAu(b); }} />
      <EtatRequete requete={r} vide={(d) => !d.donnees.length} titreVide="Aucun client" enfants={(d) => (
        <Table
          legende="Rentabilité réalisée par client"
          lignes={d.donnees}
          cle={(l) => l.entrepriseId!}
          onLigne={(l) => nav(`/admin/clients/${l.entrepriseId}?onglet=rentabilite`)}
          colonnes={[
            { cle: 'c', titre: 'Client', rendu: (l) => <Link to={`/admin/clients/${l.entrepriseId}?onglet=rentabilite`} onClick={(e) => e.stopPropagation()}>{l.raisonSociale}</Link> },
            { cle: 'n', titre: 'Cmd réalisées', num: true, rendu: (l) => l.nbCommandes },
            { cle: 'v', titre: 'Ventes nettes HT', num: true, rendu: (l) => euros(l.ventesNettesHtCentimes) },
            { cle: 'm', titre: 'Marge brute', num: true, rendu: (l) => (l.statut === 'A_COMPLETER' ? <><span className="my-num">{euros(l.margeBruteCentimes)}</span> <Badge ton="alerte">À compléter</Badge></> : euros(l.margeBruteCentimes)) },
            { cle: 't', titre: 'Marge %', num: true, rendu: (l) => taux(l.tauxMargeBruteBp) },
            { cle: 'r', titre: 'Marge contributive', num: true, rendu: (l) => { const v = (l as Rentabilite).margeContributiveCentimes ?? l.resultatCentimes; return <span style={(v ?? 0) < 0 ? { color: 'var(--erreur)', fontWeight: 600 } : undefined}>{euros(v)}</span>; } },
            { cle: 'e', titre: 'En cours HT', num: true, rendu: (l) => euros(l.enCoursHtCentimes) },
            { cle: 'd', titre: 'Devis HT', num: true, rendu: (l) => euros(l.devisHtCentimes) },
          ]}
        />
      )} />
    </div>
  );
}

function CompteResultat({ r }: { r: Rentabilite }) {
  const ligne = (libelle: string, v: number | null | undefined, o: { signe?: '-' | '+'; fort?: boolean; testid?: string } = {}) => (
    <>
      <dt style={o.fort ? { fontWeight: 600, color: 'var(--texte-principal)' } : undefined}>{o.signe === '-' ? '− ' : o.signe === '+' ? '+ ' : ''}{libelle}</dt>
      <dd className={o.fort ? 'my-prix' : 'my-num'} data-testid={o.testid}>{montant(v)}</dd>
    </>
  );
  return (
    <dl className="my-dl">
      {ligne('Ventes brutes HT (prix de grille)', r.ventesBrutesHtCentimes)}
      {ligne('Remises consenties', r.remisesHtCentimes, { signe: '-' })}
      {ligne('Avoirs sur retours', r.retoursAvoirsHtCentimes, { signe: '-' })}
      {ligne('Avoirs commerciaux', r.avoirsCommerciauxHtCentimes, { signe: '-' })}
      {ligne('Ventes nettes HT', r.ventesNettesHtCentimes, { fort: true, testid: 'ventes-nettes' })}
      {ligne('Coût des marchandises sorties (figé)', r.coutMarchandisesVenduesCentimes, { signe: '-' })}
      {ligne('Coût repris (retours remis en stock)', r.coutReprisRetoursCentimes, { signe: '+' })}
      <dt className="my-secondaire">dont pertes sur casse (non reprises)</dt><dd className="my-num" data-testid="pertes-casse">{euros(r.pertesCasseCentimes)}</dd>
      {ligne('Marge brute', r.margeBruteCentimes, { fort: true, testid: 'marge-brute' })}
      <dt>Taux de marge brute</dt><dd className="my-num">{taux(r.tauxMargeBruteBp)}</dd>
      {ligne('Frais de port facturés', r.fraisPortFacturesHtCentimes, { signe: '+' })}
      {(r.coutsDirects ?? []).map((c) => <Fragment key={c.code}>{ligne(c.nature === 'LIVRAISON' && (r.transportEstimeNbCommandes ?? 0) > 0 ? `${c.libelle} (estimé sur ${r.transportEstimeNbCommandes} commande${(r.transportEstimeNbCommandes ?? 0) > 1 ? 's' : ''})` : c.libelle!, c.montantCentimes, { signe: '-' })}</Fragment>)}
      {ligne('Marge contributive', r.margeContributiveCentimes ?? r.resultatCentimes, { fort: true, testid: 'resultat' })}
      <dt>Taux de marge contributive</dt><dd className="my-num">{taux(r.tauxMargeContributiveBp)}</dd>
      <dt>Bénéfice net comptable</dt><dd className="my-secondaire" data-testid="benefice-net">Non calculé</dd>
    </dl>
  );
}

/** Onglet « Rentabilité » de la fiche client. */
export function RentabiliteClientOnglet({ entrepriseId }: { entrepriseId: string }) {
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const r = useQuery({ queryKey: ['admin', 'marges', 'client', entrepriseId, du, au], queryFn: () => get<RentabiliteClient>(`/admin/clients/${entrepriseId}/rentabilite`, { du, au }) });
  return (
    <div className="my-pile">
      <FiltrePeriode du={du} au={au} onChange={(a, b) => { setDu(a); setAu(b); }} />
      <EtatRequete requete={r} enfants={(d) => (
        <>
          {d.realise?.statut === 'A_COMPLETER' && <Alert ton="alerte" titre="Marge à compléter">{d.realise.lignesACompleter} ligne(s) vendue(s) sans coût figé : la marge présentée est incomplète.</Alert>}
          <div className="my-grille-2">
            <Card as="section" aria-labelledby="t-realise">
              <h2 id="t-realise" className="my-titre-4" style={{ marginBottom: 12 }}>Ventes réalisées <span className="my-secondaire my-petit">— {d.realise?.nbCommandes} commande(s) sortie(s) du stock</span></h2>
              <CompteResultat r={d.realise!} />
            </Card>
            <div className="my-pile">
              <Card as="section" aria-labelledby="t-encours">
                <h2 id="t-encours" className="my-titre-4">Commandes en cours</h2>
                <p className="my-petit my-secondaire">Payées, non encore sorties du stock : non comptées dans le réalisé.</p>
                <dl className="my-dl" style={{ marginTop: 8 }}>
                  <dt>Commandes</dt><dd className="my-num">{d.enCours?.nbCommandes}</dd>
                  <dt>Ventes HT</dt><dd className="my-num">{euros(d.enCours?.ventesHtCentimes)}</dd>
                  <dt>Marge prévisionnelle</dt><dd><CelluleMarge marge={d.enCours?.margePrevisionnelleCentimes} statut={d.enCours?.statut} /></dd>
                </dl>
              </Card>
              <Card as="section" aria-labelledby="t-devis">
                <h2 id="t-devis" className="my-titre-4">Devis et commandes non payées</h2>
                <p className="my-petit my-secondaire">{d.devis?.libelle}</p>
                <dl className="my-dl" style={{ marginTop: 8 }}>
                  <dt>Nombre</dt><dd className="my-num">{d.devis?.nbCommandes}</dd>
                  <dt>Montant HT</dt><dd className="my-num">{euros(d.devis?.montantHtCentimes)}</dd>
                </dl>
              </Card>
            </div>
          </div>
          <Table
            legende="Commandes réalisées de la période"
            lignes={d.commandes ?? []}
            cle={(c) => c.id!}
            colonnes={[
              { cle: 'r', titre: 'Commande', rendu: (c) => <Link to={`/admin/commandes/${c.id}`}>{c.reference}</Link> },
              { cle: 'd', titre: 'Sortie de stock', rendu: (c) => dateCourte(c.dateRealisation) },
              { cle: 's', titre: 'Statut', rendu: (c) => <StatutBadge statut={c.statut!} /> },
              { cle: 'm', titre: 'Total HT', num: true, rendu: (c) => euros(c.totalHtCentimes) },
              { cle: 'x', titre: 'Retours / avoirs', num: true, rendu: (c) => `${c.retours} / ${c.avoirs}` },
              { cle: 'a', titre: 'Coût', rendu: (c) => (c.aCompleter ? <Badge ton="alerte">Marge à compléter</Badge> : <Badge ton="succes">Figé</Badge>) },
            ]}
          />
        </>
      )} />
    </div>
  );
}

// ───────────────────────────── Marges d'une commande ─────────────────────────────

const STATUT_COUT: Record<string, { ton: 'succes' | 'info' | 'alerte'; libelle: string }> = {
  FIGE: { ton: 'succes', libelle: 'Coût figé' },
  PREVISIONNEL: { ton: 'info', libelle: 'Prévisionnel' },
  A_COMPLETER: { ton: 'alerte', libelle: 'Marge à compléter' },
};

export function MargesCommandeCarte({ commandeId }: { commandeId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { peut } = useSession();
  const cle = ['admin', 'marges', 'commande', commandeId];
  const r = useQuery({ queryKey: cle, queryFn: () => get<MargesCommande>(`/admin/commandes/${commandeId}/marges`) });
  const [retour, setRetour] = useState(false);
  const [avoir, setAvoir] = useState(false);
  const completer = useMutation({
    mutationFn: () => post<MargesCommande>(`/admin/commandes/${commandeId}/couts/completer`),
    onSuccess: (d) => { qc.setQueryData(cle, d); toast({ ton: 'succes', titre: 'Coûts complétés', texte: 'Seules les lignes sans coût ont été figées au tarif actuel.' }); },
  });
  return (
    <Card as="section" aria-labelledby="t-marges">
      <EtatRequete requete={r} enfants={(m) => (
        <div className="my-pile">
          <div className="my-ligne my-ligne--espace">
            <h2 id="t-marges" className="my-titre-4">Marge de la commande</h2>
            <div className="my-ligne">
              {m.realisee && peut('retour.enregistrer') && <Button variante="secondaire" icone={Undo2} onClick={() => setRetour(true)}>Enregistrer un retour</Button>}
              {m.realisee && peut('retour.enregistrer') && <Button variante="tertiaire" icone={Plus} onClick={() => setAvoir(true)}>Avoir commercial</Button>}
              {m.realisee && m.lignesACompleter! > 0 && peut('couts.parametrer') && <Button variante="tertiaire" icone={Wrench} chargement={completer.isPending} onClick={() => completer.mutate()}>Compléter les coûts manquants</Button>}
            </div>
          </div>
          <p className="my-petit my-secondaire">{m.realisee ? 'Coût de revient figé à la sortie de stock : un nouveau tarif fournisseur ne modifie pas cette vente.' : 'Marchandise non sortie : coût prévisionnel au tarif actuel (il sera figé à la remise ou à l’expédition).'}</p>
          <ErreurFormulaire erreur={completer.error} />
          <Table
            legende="Marge par ligne"
            lignes={m.lignes ?? []}
            cle={(l) => l.ligneId!}
            colonnes={[
              { cle: 'p', titre: 'Produit', rendu: (l) => <>{l.libelle}<br /><span className="my-legende my-secondaire">{LIBELLE_CONDITIONNEMENT[l.conditionnementType!]} × {l.quantite}{l.quantiteRetourneeUnitesBase ? ` · ${l.quantiteRetourneeUnitesBase} u. retournées` : ''}</span></> },
              { cle: 'pu', titre: 'PU net HT', num: true, rendu: (l) => <>{euros(l.prixUnitaireNetHtCentimes)}{l.prixUnitaireAvantRemiseHtCentimes && l.prixUnitaireAvantRemiseHtCentimes > l.prixUnitaireNetHtCentimes! ? <><br /><span className="my-prix-barre my-legende">{euros(l.prixUnitaireAvantRemiseHtCentimes)}</span></> : null}</> },
              { cle: 'v', titre: 'Vente nette HT', num: true, rendu: (l) => euros(l.venteNetteHtCentimes) },
              { cle: 'c', titre: 'Coût de revient', num: true, rendu: (l) => montant(l.coutRevientTotalCentimes) },
              { cle: 'm', titre: 'Marge', num: true, rendu: (l) => <CelluleMarge marge={l.margeCentimes} statut={l.statut} /> },
              { cle: 't', titre: '%', num: true, rendu: (l) => (l.statut === 'A_COMPLETER' ? '—' : taux(l.tauxBp)) },
              { cle: 's', titre: 'Coût', rendu: (l) => <span title={l.coutFigeLe ? `Figé le ${dateHeure(l.coutFigeLe)}` : undefined}><Badge ton={STATUT_COUT[l.coutStatut!]!.ton}>{STATUT_COUT[l.coutStatut!]!.libelle}</Badge></span> },
            ]}
          />
          <dl className="my-dl">
            <dt>Ventes nettes HT</dt><dd className="my-num">{euros(m.totalVenteNetteHtCentimes)}</dd>
            <dt>Coût de revient total</dt><dd className="my-num">{montant(m.totalCoutRevientCentimes)}</dd>
            <dt><strong>Marge totale</strong></dt><dd data-testid="marge-totale"><CelluleMarge marge={m.totalMargeCentimes} statut={m.totalStatut} /> {m.totalStatut === 'OK' && <span className="my-secondaire">({taux(m.totalTauxBp)})</span>}</dd>
            {m.rentabilite && <><dt>Marge contributive (après retours, avoirs et coûts variables)</dt><dd className="my-num" data-testid="resultat-commande">{euros((m.rentabilite as Rentabilite).margeContributiveCentimes ?? m.rentabilite.resultatCentimes)}</dd></>}
          </dl>
          {(m.retours?.length || m.avoirs?.length) ? (
            <div className="my-pile my-pile--serre">
              <h3 className="my-titre-4">Retours et avoirs</h3>
              <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="retours-avoirs">
                {(m.retours ?? []).map((x) => (
                  <li key={x.id}><strong>{x.numeroAvoir}</strong> — {dateCourte(x.date)} — {euros(x.montantAvoirHtCentimes)} HT — {x.motif} — {(x.lignes ?? []).map((l) => `${l.quantiteUnitesBase} u. ${l.destination === 'CASSE' ? 'cassées (perte)' : 'remises en stock'}`).join(', ')}</li>
                ))}
                {(m.avoirs ?? []).map((x) => <li key={x.id}><strong>{x.numeroAvoir}</strong> — {dateCourte(x.date)} — avoir commercial {euros(x.montantHtCentimes)} HT — {x.motif}</li>)}
              </ul>
            </div>
          ) : null}
          {retour && <ModaleRetour m={m} onFermer={() => setRetour(false)} onFait={(d) => { qc.setQueryData(cle, d); setRetour(false); void qc.invalidateQueries({ queryKey: ['admin'] }); toast({ ton: 'succes', titre: 'Retour enregistré', texte: 'Avoir émis ; stock et coûts mis à jour.' }); }} />}
          {avoir && <ModaleAvoir commandeId={commandeId} onFermer={() => setAvoir(false)} onFait={(d) => { qc.setQueryData(cle, d); setAvoir(false); toast({ ton: 'succes', titre: 'Avoir commercial émis' }); }} />}
        </div>
      )} />
    </Card>
  );
}

function ModaleRetour({ m, onFermer, onFait }: { m: MargesCommande; onFermer: () => void; onFait: (d: MargesCommande) => void }) {
  const cleIdem = useRef(nouvelleCleIdempotence());
  const [motif, setMotif] = useState('');
  const [saisies, setSaisies] = useState<Record<string, { stock: string; casse: string }>>({});
  const lignes = useMemo(() => (m.lignes ?? []).flatMap((l) => {
    const s = saisies[l.ligneId!] ?? { stock: '', casse: '' };
    return [
      ...(Number(s.stock) > 0 ? [{ ligneCommandeId: l.ligneId!, quantiteUnitesBase: Math.round(Number(s.stock)), destination: 'REMIS_EN_STOCK' as const }] : []),
      ...(Number(s.casse) > 0 ? [{ ligneCommandeId: l.ligneId!, quantiteUnitesBase: Math.round(Number(s.casse)), destination: 'CASSE' as const }] : []),
    ];
  }), [m.lignes, saisies]);
  const envoi = useMutation({ mutationFn: () => post<MargesCommande>(`/admin/commandes/${m.commandeId}/retours`, { motif: motif.trim(), lignes }, { idempotence: cleIdem.current }), onSuccess: onFait });
  useEffect(() => { cleIdem.current = nouvelleCleIdempotence(); }, [lignes, motif]);
  const champ = (id: string, k: 'stock' | 'casse', label: string) => (
    <TextField label={label} inputMode="numeric" value={saisies[id]?.[k] ?? ''} onChange={(e) => setSaisies((x) => ({ ...x, [id]: { ...(x[id] ?? { stock: '', casse: '' }), [k]: e.target.value.replace(/\D/g, '') } }))} />
  );
  return (
    <Modal ouverte titre="Enregistrer un retour" onFermer={onFermer} large actions={<><Button variante="tertiaire" onClick={onFermer}>Annuler</Button><Button chargement={envoi.isPending} disabled={!lignes.length || motif.trim().length < 3} onClick={() => envoi.mutate()}>Enregistrer et émettre l’avoir</Button></>}>
      <div className="my-pile">
        <p className="my-petit">Quantités en unités de base (une bouteille, un paquet…). Remis en stock : coût repris. Casse : perte conservée dans le coût.</p>
        {(m.lignes ?? []).map((l) => (
          <fieldset key={l.ligneId} className="my-pile my-pile--serre" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="my-field__label">{l.libelle} — {LIBELLE_CONDITIONNEMENT[l.conditionnementType!]} × {l.quantite}</legend>
            <div className="my-form-ligne">
              {champ(l.ligneId!, 'stock', 'Unités remises en stock')}
              {champ(l.ligneId!, 'casse', 'Unités cassées (perte)')}
            </div>
          </fieldset>
        ))}
        <TextField label="Motif du retour" value={motif} onChange={(e) => setMotif(e.target.value)} required />
        <ErreurFormulaire erreur={envoi.error} />
      </div>
    </Modal>
  );
}

function ModaleAvoir({ commandeId, onFermer, onFait }: { commandeId: string; onFermer: () => void; onFait: (d: MargesCommande) => void }) {
  const cleIdem = useRef(nouvelleCleIdempotence());
  const [montantSaisi, setMontant] = useState('');
  const [motif, setMotif] = useState('');
  const centimes = versCentimes(montantSaisi);
  const envoi = useMutation({ mutationFn: () => post<MargesCommande>(`/admin/commandes/${commandeId}/avoirs`, { montantHtCentimes: centimes, motif: motif.trim() }, { idempotence: cleIdem.current }), onSuccess: onFait });
  return (
    <Modal ouverte titre="Avoir commercial" onFermer={onFermer} actions={<><Button variante="tertiaire" onClick={onFermer}>Annuler</Button><Button chargement={envoi.isPending} disabled={!(centimes > 0) || motif.trim().length < 3} onClick={() => envoi.mutate()}>Émettre l’avoir</Button></>}>
      <div className="my-pile">
        <p className="my-petit">Sans retour de marchandise : réduit les ventes nettes, sans effet sur le coût.</p>
        <TextField label="Montant HT (€)" inputMode="decimal" value={montantSaisi} onChange={(e) => setMontant(e.target.value)} />
        <TextField label="Motif" value={motif} onChange={(e) => setMotif(e.target.value)} />
        <ErreurFormulaire erreur={envoi.error} />
      </div>
    </Modal>
  );
}

// ───────────────────────────── Paramètres de coûts ─────────────────────────────

const NATURES = ['TRANSPORT_ENTRANT', 'DROITS_ACCISES', 'MANUTENTION', 'STOCKAGE', 'EMBALLAGE', 'PREPARATION', 'LIVRAISON', 'FRAIS_PAIEMENT', 'COMMISSION', 'AUTRE'] as const;
const LIBELLE_NATURE: Record<string, string> = { TRANSPORT_ENTRANT: 'Transport entrant', DROITS_ACCISES: 'Droits et accises', MANUTENTION: 'Manutention', STOCKAGE: 'Stockage', EMBALLAGE: 'Emballage', PREPARATION: 'Préparation', LIVRAISON: 'Livraison', FRAIS_PAIEMENT: 'Frais de paiement', COMMISSION: 'Commission', AUTRE: 'Autre' };
const MODES: Record<string, { libelle: string; unite: '%' | '€'; affectation: 'COUT_REVIENT' | 'COUT_DIRECT' }> = {
  POURCENT_ACHAT: { libelle: '% du prix d’achat', unite: '%', affectation: 'COUT_REVIENT' },
  FIXE_PAR_UNITE: { libelle: '€ par unité', unite: '€', affectation: 'COUT_REVIENT' },
  FIXE_PAR_CARTON: { libelle: '€ par carton', unite: '€', affectation: 'COUT_REVIENT' },
  FIXE_PAR_PALETTE: { libelle: '€ par palette', unite: '€', affectation: 'COUT_REVIENT' },
  POURCENT_VENTES: { libelle: '% des ventes nettes', unite: '%', affectation: 'COUT_DIRECT' },
  FIXE_PAR_COMMANDE: { libelle: '€ par commande', unite: '€', affectation: 'COUT_DIRECT' },
  FIXE_PAR_PALETTE_VENDUE: { libelle: '€ par palette vendue', unite: '€', affectation: 'COUT_DIRECT' },
};
const versSaisie = (f: Frais) => String((f.valeur ?? 0) / 100).replace('.', ',');

function OngletParametres() {
  const qc = useQueryClient();
  const toast = useToast();
  const { peut } = useSession();
  const modifiable = peut('couts.parametrer');
  const r = useQuery({ queryKey: ['admin', 'marges', 'parametres'], queryFn: () => get<ParametresCouts>('/admin/marges/parametres') });
  const [frais, setFrais] = useState<Frais[] | null>(null);
  const [valeurs, setValeurs] = useState<Record<number, string>>({});
  useEffect(() => { if (r.data && frais === null) { setFrais(r.data.frais ?? []); setValeurs(Object.fromEntries((r.data.frais ?? []).map((f, i) => [i, versSaisie(f)]))); } }, [r.data, frais]);
  const enregistrer = useMutation({
    mutationFn: () => put<ParametresCouts>('/admin/marges/parametres', { frais: frais!.map((f, i) => ({ ...f, valeur: Math.round(Number((valeurs[i] ?? '0').replace(',', '.')) * 100) })) }, { version: r.data!.version }),
    onSuccess: (d) => { qc.setQueryData(['admin', 'marges', 'parametres'], d); setFrais(null); void qc.invalidateQueries({ queryKey: ['admin', 'marges'] }); toast({ ton: 'succes', titre: 'Paramètres de coûts enregistrés', texte: 'Applicables aux prochaines sorties de stock ; les coûts déjà figés ne changent pas.' }); },
  });
  const modifier = (i: number, p: Partial<Frais>) => setFrais((l) => l!.map((f, j) => (j === i ? { ...f, ...p } : f)));
  const erreursChamps = enregistrer.error instanceof ApiErreur ? ((enregistrer.error.probleme?.erreurs as { champ: string; message: string }[] | undefined) ?? []) : [];
  return (
    <EtatRequete requete={r} enfants={(p) => (
      <div className="my-pile">
        <Alert ton="info" titre="Règle anti double comptage">
          Chaque frais est compté <strong>soit</strong> dans le coût de revient (achat + frais d’approche), <strong>soit</strong> en coût direct de vente déduit de la marge — jamais les deux. Une même nature de frais ne peut être active qu’une fois.
        </Alert>
        {p.modifieLe && <p className="my-legende my-secondaire">Dernière modification : {dateHeure(p.modifieLe)} par {p.modifiePar} · version {p.version}</p>}
        {(['COUT_REVIENT', 'COUT_DIRECT'] as const).map((aff) => (
          <Card as="section" key={aff} aria-labelledby={`t-${aff}`}>
            <h2 id={`t-${aff}`} className="my-titre-4" style={{ marginBottom: 12 }}>{aff === 'COUT_REVIENT' ? 'Frais inclus dans le coût de revient' : 'Coûts directs de vente déduits de la marge'}</h2>
            <div className="my-pile">
              {(frais ?? []).map((f, i) => f.affectation !== aff ? null : (
                <div key={i} className="my-form-ligne" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', alignItems: 'end', borderBottom: 'var(--border-fin) solid var(--bordure)', paddingBottom: 12 }} data-testid={`frais-${f.code}`}>
                  <TextField label="Libellé" value={f.libelle ?? ''} disabled={!modifiable} onChange={(e) => modifier(i, { libelle: e.target.value })} />
                  <SelectField label="Nature" value={f.nature} disabled={!modifiable} onChange={(e) => modifier(i, { nature: e.target.value as Frais['nature'] })} options={NATURES.map((n) => ({ value: n, label: LIBELLE_NATURE[n]! }))} />
                  <SelectField label="Calcul" value={f.mode} disabled={!modifiable} onChange={(e) => modifier(i, { mode: e.target.value as Frais['mode'] })} options={Object.entries(MODES).filter(([, m]) => m.affectation === aff).map(([v, m]) => ({ value: v, label: m.libelle }))} />
                  <TextField label={`Valeur (${MODES[f.mode!]?.unite ?? ''})`} inputMode="decimal" value={valeurs[i] ?? ''} disabled={!modifiable} onChange={(e) => setValeurs((v) => ({ ...v, [i]: e.target.value }))} />
                  {aff === 'COUT_DIRECT' && (
                    <SelectField label="Appliqué si" value={f.conditions?.modeRemise ?? (f.conditions?.moyensPaiement?.length ? 'CB' : '')} disabled={!modifiable} onChange={(e) => modifier(i, { conditions: e.target.value === 'LIVRAISON' || e.target.value === 'RETRAIT' ? { modeRemise: e.target.value } : e.target.value === 'CB' ? { moyensPaiement: ['CB_EN_LIGNE', 'CB_RETRAIT'] } : null })} options={[{ value: '', label: 'Toutes les commandes' }, { value: 'LIVRAISON', label: 'Livraison uniquement' }, { value: 'RETRAIT', label: 'Retrait uniquement' }, { value: 'CB', label: `${LIBELLE_MOYEN.CB_EN_LIGNE} ou ${LIBELLE_MOYEN.CB_RETRAIT}` }]} />
                  )}
                  <div className="my-ligne">
                    <Choice label="Actif" checked={!!f.actif} disabled={!modifiable} onChange={(e) => modifier(i, { actif: e.target.checked })} />
                    {modifiable && <Button variante="tertiaire" compact icone={Trash2} aria-label={`Supprimer ${f.libelle}`} onClick={() => { setFrais((l) => l!.filter((_, j) => j !== i)); setValeurs((v) => Object.fromEntries(Object.entries(v).filter(([k]) => Number(k) !== i).map(([k, x]) => [Number(k) > i ? Number(k) - 1 : Number(k), x]))); }}>Supprimer</Button>}
                  </div>
                  {erreursChamps.filter((e) => e.champ.startsWith(`${f.code}.`)).map((e) => <p key={e.champ} className="my-field__erreur" role="alert" style={{ gridColumn: '1 / -1' }}>{e.message}</p>)}
                </div>
              ))}
              {modifiable && (
                <Button variante="secondaire" icone={Plus} onClick={() => { const i = frais!.length; setFrais((l) => [...l!, { code: `FRAIS_${i + 1}`, libelle: 'Nouveau frais', nature: 'AUTRE', affectation: aff, mode: aff === 'COUT_REVIENT' ? 'POURCENT_ACHAT' : 'FIXE_PAR_COMMANDE', valeur: 0, actif: true }]); setValeurs((v) => ({ ...v, [i]: '0' })); }}>
                  Ajouter un frais
                </Button>
              )}
            </div>
          </Card>
        ))}
        <ErreurFormulaire erreur={enregistrer.error} />
        {modifiable ? <div className="my-ligne"><Button icone={Save} chargement={enregistrer.isPending} onClick={() => enregistrer.mutate()}>Enregistrer les paramètres</Button><Button variante="tertiaire" onClick={() => setFrais(null)}>Annuler les modifications</Button></div>
          : <p className="my-petit my-secondaire">Lecture seule : la modification des coûts est réservée au super-administrateur et à la comptabilité.</p>}
      </div>
    )} />
  );
}

